import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface PaginationProps {
  // Sayfa bilgileri
  currentPage: number; // 0-indexed
  totalPages: number;
  pageSize: number;

  // Satır bilgileri
  selectedRowCount?: number;
  totalRowCount: number;

  // Sayfa boyutu seçenekleri
  pageSizeOptions?: number[];

  // Callback fonksiyonları
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;

  // Navigasyon kontrolleri
  canPreviousPage: boolean;
  canNextPage: boolean;
  lessItems?: boolean;
}

export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  selectedRowCount = 0,
  totalRowCount,
  pageSizeOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  onPageChange,
  onPageSizeChange,
  canPreviousPage,
  canNextPage,
  lessItems = false,
}: PaginationProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {selectedRowCount} - {totalRowCount} {t("row_selected")}.
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        {!lessItems && (
          <>
            <div className="flex items-center space-x-2">
              <p className="text-sm font-600">{t("rows_per_page")}</p>
              <Select
                value={`${pageSize}`}
                onValueChange={(value) => onPageSizeChange(Number(value))}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {currentLang === "en" && (
          <div className="flex w-[100px] items-center justify-center text-sm font-600">
            Page {currentPage} of {totalPages}
          </div>
        )}
        {currentLang === "tr" && (
          <div className="flex w-[150px] items-center justify-center text-sm font-600">
            Sayfa {currentPage} / {totalPages}
          </div>
        )}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => onPageChange(0)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => canPreviousPage && onPageChange(currentPage - 2)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => canNextPage && onPageChange(currentPage)}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => onPageChange(totalPages - 1)}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
